<script>
import { mapActions, mapGetters } from "vuex";
import cardValidator from "card-validator";

import documentsValidator from "@/utils/validators/documents";
import paymentValidator from "@/utils/validators/payments";
import errorMixin from "@/mixins/errorMixin";
import scrollMixin from "@/mixins/scrollMixin";

import Tooltip from "@/components/_Shared/Tooltip";

export default {
  mixins: [errorMixin, scrollMixin],

  components: {
    Tooltip
  },

  data() {
    return {
      number: null,
      deadline_month: null,
      deadline_year: null,
      cvc: null,
      name: null,
      document: null,
      birthdate: null,
      phone: {
        country_code: "55",
        area_code: null,
        number: null
      },
      store: false,
      phone_number: null,
      flag: null,
      preloader: false,
      error: null
    };
  },

  methods: {
    ...mapActions("payable", [
      "setPaymentData",
      "createOnGateway",
      "createPayment"
    ]),

    isCreditCardInserted(type) {
      return this.flag === type;
    },

    async submit() {
      this.error = null;

      this.preloader = true;

      try {
        await this.validateCardData();
        await this.validateDocumentData();

        await this.setPaymentData(this.$data);

        if (this.notCreatedOnGateway) {
          await this.createOnGateway();
        } else {
          await this.createPayment();
        }
      } catch (e) {
        this.error = e;
      }

      this.preloader = false;
    },

    async validateCardData() {
      const validForm = await this.$validator.validateAll();
      const ccValidator = await cardValidator.number(this.number);

      if (!validForm) {
        throw "Ops! Alguma informação do cartão pode estar incorreta, verifique os dados informados!";
      }

      if (!ccValidator.isValid) {
        this.createError("number", "Número inválido!");
        throw "Dados do cartão de crédito inválido!";
      }

      if (!paymentValidator.isCreditCardFlagAccepted(this.flag)) {
        this.createError("number", "Número inválido ou não aceito!");
        throw "O cartão informado não é aceito!";
      }
    },

    async validateDocumentData() {
      const documentIsValid = documentsValidator.validate(this.document);

      if (!documentIsValid) {
        this.createError("document", "Documento inválido!");
        throw "O CPF ou CNPJ informado é inválido!";
      }
    }
  },

  computed: {
    ...mapGetters("payable", ["notCreatedOnGateway"]),

    months: function() {
      return [
        "01",
        "02",
        "03",
        "04",
        "05",
        "06",
        "07",
        "08",
        "09",
        "10",
        "11",
        "12"
      ];
    },

    currentYear: function() {
      return new Date().getFullYear();
    },

    cantPay: function() {
      const fields = [
        this.number,
        this.deadline_month,
        this.deadline_year,
        this.cvc,
        this.name,
        this.document,
        this.birthdate,
        this.phone_number
      ];

      return fields.some(field => !field);
    }
  },

  watch: {
    number(number) {
      const creditCard = cardValidator.number(number);

      this.flag = creditCard.card ? creditCard.card.type : null;

      if (
        number.length > 8 &&
        !paymentValidator.isCreditCardFlagAccepted(this.flag)
      ) {
        this.createError("number", "Número inválido ou cartão não aceito!");
        this.error = "Número inválido ou cartão não aceito!";
      }
    },

    phone_number(phone_number) {
      if (phone_number.length > 9) {
        this.phone.area_code = phone_number.substr(0, 2);
        this.phone.number = phone_number.substr(2, phone_number.length);
      }
    }
  },

  async mounted() {
    this.scrollTo("#credit-card");
  }
};
</script>

<template>
  <div id="credit-card">
    <div class="credit-cards">
      <span>Bandeiras aceitas:</span>
      <div class="logos">
        <img :class="{ 'no-flag': !flag, 'cc-selected': isCreditCardInserted('visa') }" src="/static/credit-card/visa.svg" alt="Cartão bandeira Visa" />
        <img :class="{ 'no-flag': !flag, 'cc-selected': isCreditCardInserted('mastercard') }" src="/static/credit-card/mastercard.svg" alt="Cartão bandeira Mastercard" />
        <img :class="{ 'no-flag': !flag, 'cc-selected': isCreditCardInserted('american-express') }" src="/static/credit-card/amex.svg" alt="Cartão bandeira Amex" />
        <img :class="{ 'no-flag': !flag, 'cc-selected': isCreditCardInserted('elo') }" src="/static/credit-card/elo.svg" alt="Cartão bandeira Elo" />
        <img :class="{ 'no-flag': !flag, 'cc-selected': isCreditCardInserted('diners-club') }" src="/static/credit-card/diners.svg" alt="Cartão bandeira Diners" />
        <img :class="{ 'no-flag': !flag, 'cc-selected': isCreditCardInserted('hiper') }" src="/static/credit-card/hiper.svg" alt="Cartão bandeira Hiper" />
        <img :class="{ 'no-flag': !flag, 'cc-selected': isCreditCardInserted('hipercard') }" src="/static/credit-card/hipercard.svg" alt="Cartão bandeira Hipercard" />
      </div>
    </div>

    <div class="warning" v-if="error">{{error}}</div>

    <div class="fields">
      <div class="field" :class="{ 'has-error': errors.first('number') }">
        <span>Número do cartão</span>

        <the-mask
          class="ym-disable-keys"
          type="text"
          name="number"
          mask="#### #### #### ####"
          placeholder="0000 0000 0000 0000"
          v-validate="'required'"
          data-vv-as="Número do cartão"
          v-model="number"
        />
      </div>

      <div class="expiration-and-cvv">
        <div class="field" :class="{'has-error': errors.first('deadline_month') || errors.first('deadline_year')}">
          <span>Data de expiração</span>
          <br />
          <select class="ym-disable-keys" name="deadline_month" v-validate="'required'" v-model="deadline_month">
            <option value="null">Mês</option>
            <option v-for="month in months" :value="month">{{ month }}</option>
          </select>

          <select
            class="ym-disable-keys"
            name="deadline_year"
            v-validate="'required'"
            v-model="deadline_year"
          >
            <option value="null">Ano</option>
            <option v-for="year in 20" :value="currentYear + year - 1">{{ currentYear + year - 1 }}</option>
          </select>
        </div>

        <div class="field cvv" :class="{'has-error': errors.first('cvc')}">
          <span>Código CVV</span>
          <div class=" tooltip-position">
            <Tooltip></Tooltip>
          </div>

          <input
            class="ym-disable-keys"
            type="text"
            name="cvc"
            placeholder="000"
            maxlength="4"
            v-validate="'required|min:3|max:4|numeric'"
            v-model="cvc"
          />
        </div>
      </div>
    </div>

    <div class="fields">
      <div class="field" :class="{'has-error': errors.first('name')}">
        <span>Nome do titular do cartão</span>
        <input
          class="ym-disable-keys"
          type="text"
          name="name"
          placeholder="Paula S Silva"
          v-validate="'required'"
          data-vv-as="Nome do titular do cartão"
          v-model="name"
        />
      </div>

      <div class="field" :class="{'has-error': errors.first('document')}">
        <span>CPF ou CNPJ do titular</span>
        <the-mask
          class="ym-disable-keys"
          type="text"
          name="document"
          placeholder="000.000.000-00"
          v-validate="'required|min:11|max:14'"
          data-vv-as="CPF ou CNPJ do titular"
          :mask="['###.###.###-##', '##.###.###/####-##']"
          :masked="false"
          v-model="document"
        />
      </div>

      <div class="birthdate-and-phone">
        <div class="field" :class="{'has-error': errors.first('birthdate')}">
          <span>Data de nascimento</span>
          <input
            class="ym-disable-keys"
            type="text"
            name="birthdate"
            v-validate="'required|date_format:DD/MM/YYYY'"
            data-vv-as="Data de nascimento"
            placeholder="00/00/0000"
            v-mask="'##/##/####'"
            v-model="birthdate"
          />
        </div>

        <div class="field" :class="{'has-error': errors.first('phone')}">
          <span>Telefone</span>
          <the-mask
            class="ym-disable-keys"
            type="text"
            name="phone"
            v-validate="'required|numeric|min:10|max:11'"
            :mask="['(##) ####-####', '(##) #####-####']"
            placeholder="(00) 00000-0000"
            v-model="phone_number"
          />
        </div>
      </div>
    </div>

    <label class="checkbox">
      <input
        class="save-checbox"
        :error="errors.first('store')"
        name="save-checkbox"
        v-model="store"
        type="checkbox"
      >
      <span>Salvar cartão para compras futuras.</span>
    </label>

    <button
      :disabled="cantPay || preloader"
      :class="{ 'button-disabled': cantPay || preloader, 'button-loading': preloader }"
      @click="submit"
    >
      <span :class="{ 'disabled': preloader }">PAGAR</span>
    </button>

    <div class="info">Nossos pagamentos são processados pelo PAGBANK e ASAAS.</div>
  </div>
</template>

<style scoped>
.credit-cards {
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.credit-cards span {
  font-size: 12px;
  color: #af006b;
  margin-bottom: 4px;
}

.logos {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
}

img {
  padding: 4px;
  width: 25px;
  transition: all 0.25s ease;
  opacity: 0.25;
}

.no-flag {
  opacity: 1;
  width: 40px;
}

.cc-selected {
  opacity: 1;
  width: 40px;
}

.warning {
  margin-top: 12px;
  border-left: solid 1px #af006b;
  color: #af006b;
  padding-left: 8px;
  font-size: 12px;
}

.fields {
  width: 100%;
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 12px;
}

.fields:not(:last-child) {
  margin-bottom: 12px;
}

.field span {
  color: #333;
  font-size: 13px;
  font-weight: 300;
  line-height: 16px;
}
.field input {
  width: calc(100% - 16px - 2px);
  height: 36px;
  margin-top: 4px;
  border-radius: 4px;
  border: solid 1px #e2e2e2;
  padding: 0 8px;
  transition: all 0.25s ease;
}

.field input:focus {
  border-color: var(--pink);
}

.field.has-error input {
  border: 1px solid var(--red);
}

.field.has-error span {
  color: var(--red);
}

.expiration-and-cvv {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.field select {
  height: 36px;
  width: 48%;
  border: solid 1px #ddd;
  border-radius: 4px;
  padding: 0 4px;
  margin-top: 4px;
}

.expiration-and-cvv .field {
  width: 48%;
}

.tooltip-position {
  position: relative;
  top: 12px;
  right: 5px;
}

.birthdate-and-phone {
  width: 100%;
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 12px;
}

.checkbox {
  display: flex;
  align-items: center;
  font-size: 12px;
}

.checkbox b {
  font-weight: 400;
}

.save-checbox {
  margin-right: 12px;
}

button {
  width: 100%;
  max-width: 100%;
  height: 44px;
  margin-top: 24px;
  border: 0;
  outline: none;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #af006b;
  border-radius: 4px;
  color: #fff;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.25s ease;
}

button:hover {
  background-color: var(--pink-dark);
}

.button-disabled {
  opacity: 0.25;
  cursor: default;
}

.button-loading:before {
  content: "";
  width: 12px;
  height: 12px;
  border-radius: 24px;
  background-clip: padding-box;
  border: rgba(255, 255, 255, 0.25) 2px solid;
  border-top-color: #fff;
  animation: animation-rotate 750ms linear infinite;
}

.button-loading span,
.button-loading:hover span,
.button-loading:active span {
  display: none;
}

.disabled {
  display: none;
}

.info {
  margin: 8px auto 0;
  font-size: 13px;
  text-align: center;
  color: #333;
  width: 100%;
}

@media (min-width: 600px) {
  .fields {
    grid-template-columns: 1fr 1fr;
  }

  .birthdate-and-phone {
    width: calc(200% + 12px);
  }
}
</style>



// WEBPACK FOOTER //
// src/components/_Shared/Checkout/Payment/PaymentOptions/NewCreditCard.vue